/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023-2024 (original work) Open Assessment Technologies SA ;
 */

import pluginFactory from 'taoTests/runner/plugin';
import { debounce, defaults } from 'lodash';
import lifecycle from 'page-lifecycle';
import { getNavigationFeedbackConfig } from 'testRunnerDynamicModulesIndex';
import { showNavigationFeedback, getNavigationFeedbacksStore } from '../../../feedback';
import { isPausedByProctorUiFlow } from '../../../util/proctoring.js';
import { securityLog } from '../common/securityLog.js';
import { runActionInIframesRecursively } from '../common/iframeUtil.js';
import fullscreenApiFactory from '../forceFullscreen/fullscreenApi';
import { addHideOnPrintStyle } from '../../tools/print/util.js';
import fullScreenKeyboardInputObserver from '../common/fullscreenInputObserver.js';
import { isNavigationDisabledByTimers } from '../../../timers/navigation.js';

const defaultConfig = {
    autoresume: true,
    threshold: 200 // threshold in ms, while blur is not triggered
};

export default pluginFactory({
    name: 'pauseOnBlur',
    install() {
        const testRunner = this.getTestRunner();
        const testConfig = testRunner.getConfig();
        const serviceCallId = testConfig.serviceCallId;
        const navigationFeedbacksStore = getNavigationFeedbacksStore(serviceCallId);
        const providedConfig = testRunner.getPluginConfig(this.getName()) || {};
        const pluginConfig = defaults({}, providedConfig, defaultConfig);
        const { autoresume, threshold } = pluginConfig;

        /**
         * Helper to work with browser fullscreen mode.
         */
        this.browserApi = fullscreenApiFactory();

        this.onBlur = debounce(e => {
            const isSecurityMessage = navigationFeedbacksStore.isSecurityShown();
            // isSecurityMessage used in order to prevent another modal when it is already opened
            if (
                (this.isShowed ||
                    this.isInFrame ||
                    isSecurityMessage ||
                    !this.fullscreenInputObserver?.isFullScreenAllowed()) &&
                e.newState !== 'hidden'
            ) {
                return;
            }
            // onBlur states are 'passive' and 'hidden'
            if (e.newState !== 'active' && !isPausedByProctorUiFlow(testRunner)) {
                const feedbackTypeArgs = { isSecurity: true };
                const feedbackConfig = getNavigationFeedbackConfig(feedbackTypeArgs, {
                    testRunnerPlugins: testRunner.getPlugins()
                });
                securityLog(testRunner, 'blur-attempt');
                testRunner.trigger('disablenav');
                showNavigationFeedback(feedbackTypeArgs, feedbackConfig)
                    .then(feedbackResult => {
                        this.isShowed = false;
                        const isFullscreen = this.browserApi.isFullscreen();
                        testRunner.trigger('security-closed', {
                            plugin: this.getName(),
                            autoresume
                        });
                        const disableNav = isNavigationDisabledByTimers(
                            serviceCallId,
                            testRunner.getDataHolder()?.getCurrentTestPart(),
                            testRunner.getDataHolder()?.getCurrentItem()?.id
                        );

                        if (!disableNav) {
                            testRunner.trigger('enablenav');
                        }
                        if (!feedbackResult.cancelled) {
                            if (!isFullscreen) {
                                this.browserApi.enterFullscreen();
                            }
                        }
                    })
                    .catch(err => {
                        this.isShowed = false;
                        testRunner.trigger('error', err);
                    });

                testRunner.trigger('security-showed', {
                    plugin: this.getName(),
                    autoresume,
                    action: 'pause',
                    category: 'examinee',
                    subcategory: 'navigation',
                    message: 'Test taker left test screen.'
                });
                this.isShowed = true;
            }
        }, threshold);

        this.onIframeFocus = () => {
            this.isInFrame = true;
        };

        this.onIframeBlur = () => {
            this.isInFrame = false;
            // force onBlur state to focus on parent in case iframe blur
            this.onBlur({ newState: 'active' });
        };

        this.addEventListeners = win => {
            const contentDocument = win.contentDocument || win.document;

            if (contentDocument) {
                contentDocument.addEventListener('focus', this.onIframeFocus);
                contentDocument.addEventListener('blur', this.onIframeBlur);
            }
        };
    },
    /**
     * Initialize plugin (called during runner's initialization)
     */
    init() {
        this.isShowed = false;
        this.fullscreenInputObserver = fullScreenKeyboardInputObserver();
        this.fullscreenInputObserver.observeFullScreenKeyboardInput(this.browserApi);
        lifecycle.addEventListener('statechange', this.onBlur);

        this.addEventListeners(window);
        runActionInIframesRecursively(this.addEventListeners);

        /**
         * Ensure content is not printed.
         * For items where print is enabled, `print` plugin should:
         *    override styles of this class with styles of higher priority, and hide security-modal element
         */
        this.printStyleTag = addHideOnPrintStyle();
    },
    /**
     * Destroy plugin (called during runner's destruction)
     */
    destroy() {
        lifecycle.removeEventListener('statechange', this.onBlur);
        document.removeEventListener('focus', this.onIframeFocus);
        document.removeEventListener('blur', this.onIframeBlur);
        window.contentDocument?.removeEventListener('focus', this.onIframeFocus);
        window.contentDocument?.removeEventListener('blur', this.onIframeBlur);
        this.printStyleTag?.remove();
        this.fullscreenInputObserver?.unsubscribe();
    }
});
